import React from 'react';
import { useQuery } from 'react-query';
import { Marker } from 'react-map-gl/mapbox';
import PropTypes from 'prop-types';
import lodash from 'lodash';
import { useAuthenticator } from '@aws-amplify/ui-react';

import { getUserFavoritePlatforms } from '../../../services/profile.service';
import ProfileQueryTypes from '../../../services/query-types/profile';
import MarkerStatus from '../../icons/MarkerStatus';
import { brandingConfig } from '../../../config';

const pinStyle = {
  cursor: 'pointer',
  fill: '#d00',
  stroke: 'none',
};

// Important for perf: the markers never change, avoid rerender when the map viewport changes
function Pins(props) {
  const {
    data, resetClicked, selectedPlatform, handleAction, handleClick, onClusterClick,
  } = props;

  const { authStatus } = useAuthenticator((ctx) => [ctx.authStatus]);

  const [clickedItems, setClickedItems] = React.useState({});

  const {
    data: favoritePlatformData,
  } = useQuery(
    ProfileQueryTypes.REST_PROFILE_FAVORITE_PLATFORMS,
    getUserFavoritePlatforms,
    {
      refetchOnWindowFocus: false,
      enabled: authStatus === 'authenticated',
    },
  );

  if (resetClicked && !lodash.isEmpty(clickedItems)) {
    setClickedItems({});
  }

  React.useEffect(() => {
    if (selectedPlatform) {
      const selectedIdx = data.findIndex((o) => o.properties?.org_platform_id === selectedPlatform.properties?.org_platform_id);

      if (selectedIdx > -1) {
        setClickedItems({ [selectedIdx]: true });
      }
    }
  }, [data, selectedPlatform]);

  const isFavorite = (datasetId) => {
    if (authStatus !== 'authenticated') return false;

    if (favoritePlatformData) {
      if (favoritePlatformData.msg && favoritePlatformData.msg === 'Signature verification failed') {
        console.log('WARNING: login signature failed');
        return false;
      }
      const foundFavorite = favoritePlatformData.find((fp) => fp.obs_dataset_id === datasetId);
      if (foundFavorite) {
        return true;
      }
    }

    return false;
  };

  const onClick = (index, dataset) => {
    const item = clickedItems[index];

    setClickedItems({ ...clickedItems, [index]: !item });

    handleClick(dataset);
  };

  const getClusterIcon = (properties, clicked, includesFavorite, props) => {
    const isNestPlatform = brandingConfig.filters.organizationId && data.properties?.organization_id === brandingConfig.filters.organizationId;
    const passedProps = { ...props };

    if (isNestPlatform) {
      passedProps.width = '22';
      passedProps.height = '22';
    }

    return (<MarkerStatus properties={properties} isFavorite={includesFavorite} isClicked={clicked} showText={false} iconProps={passedProps} />);
  };

  const getIcon = (dataset, isFavorite, clicked, props) => {
    const isNestPlatform = brandingConfig.filters.organizationId && data.properties?.organization_id === brandingConfig.filters.organizationId;
    const passedProps = { ...props };

    if (isNestPlatform) {
      passedProps.width = '22';
      passedProps.height = '22';
    }

    return (<MarkerStatus properties={dataset.properties} isClicked={clicked} showText={false} isFavorite={isFavorite} iconProps={passedProps} />);
  };

  return (
    data
    && data.length > 0
    && data.map((dataset, index) => {
      const [longitude, latitude] = dataset.geometry.coordinates;

      const isNestPlatform = brandingConfig.filters.organizationId && dataset.properties?.organization_id === brandingConfig.filters.organizationId;
      const BASE_SIZE = isNestPlatform ? 22 : 20;

      const {
        cluster: isCluster,
        point_count: pointCount,
        is_favorite: includesFavorite,
      } = dataset.properties;

      if (isCluster) {
        const clusterSize = includesFavorite ? Math.round((BASE_SIZE * 5) / 3) : BASE_SIZE;
        return (
          <Marker
            key={`cluster-${index}`}
            longitude={longitude}
            latitude={latitude}
            className={dataset.properties.platform_type}
          >
            {getClusterIcon(dataset.properties, clickedItems[index], includesFavorite, {
              style: {
                ...pinStyle,
                transform: 'translate(-50%, -50%)',
                position: 'absolute',
                top: '0',
                left: '0',
              },
              height: `${clusterSize}px`,
              width: `${clusterSize}px`,
              onClick: () => onClusterClick(dataset, latitude, longitude),
            })}
            <span
              className="pin-label"
            >
              +
              {pointCount}
            </span>
          </Marker>
        );
      }

      const isFav = isFavorite(dataset.properties.obs_dataset_id);
      const markerSize = isFav ? Math.round((BASE_SIZE * 5) / 3) : BASE_SIZE;

      return (
        <Marker
          key={`marker-${index}`}
          longitude={longitude}
          latitude={latitude}
          className={dataset.properties.platform_type}
        >
          {getIcon(dataset, isFav, clickedItems[index], {
            style: {
              ...pinStyle,
              transform: 'translate(-50%, -50%)',
              position: 'absolute',
              top: '0',
              left: '0',
            },
            height: `${markerSize}px`,
            width: `${markerSize}px`,
            onClick: () => onClick(index, dataset),
            onMouseEnter: () => (clickedItems[index] ? {} : handleAction(dataset)),
            onMouseLeave: () => (clickedItems[index] ? {} : handleAction()),
          })}
          {dataset.properties?.observations && (
            <span
              className="pin-label"
            >
              {dataset.properties.observations[0].value.toFixed(2)}
              %
            </span>
          )}
        </Marker>
      );
    })
  );
}

Pins.propTypes = {
  data: PropTypes.array,
  resetClicked: PropTypes.bool,
  handleAction: PropTypes.func,
  handleClick: PropTypes.func,
  onClusterClick: PropTypes.func,
};

Pins.defaultProps = {
  data: [],
  resetClicked: false,
  handleAction: () => { },
  handleClick: () => { },
  onClusterClick: () => { },
};

export default React.memo(Pins);
